import React from "react";
import styled from "styled-components";
import {
  FontWeight,
  Radius,
  Spacing,
  background,
  content,
  ellipsis,
} from "@glossy";
import { Elevation } from "@glossy/elevations";
import { AI_GRADIENT, aiBorderGradient, aiTextGradient } from "@glossy/mixins";
import Icon from "@ui-kit/icon";

export interface IProps {
  title: string;
  height?: number;
  subtitle?: React.ReactNode;
  magicFooter?: React.ReactNode;
  isAI?: boolean;
  children: React.ReactNode;
  statusInfo?: React.ReactElement;
  actions?: React.ReactNode;
  onClick?: () => void;
}

const TitleRight = styled.div`
  display: flex;
  gap: ${Spacing.ExtraSmall};
`;

const Root = styled.div<{ $isAI: boolean }>`
  position: relative;
  border-radius: ${Radius.MediumLarge};
  color: ${content};
  background-color: ${background};
  box-shadow: ${Elevation.Elevation_1};
  overflow: hidden;
  ${(props) => props.$isAI && aiBorderGradient(background(props))}
`;

const Header = styled.div<{ $isAI: boolean }>`
  padding: ${({ $isAI }) => ($isAI ? "23px" : Spacing.Large)};
  height: ${({ $isAI }) => ($isAI ? 107 : 108)}px;
`;

const Title = styled.div`
  font-size: 18px;
  height: 28px;
  font-weight: ${FontWeight.Bold};
  display: flex;
  justify-content: space-between;
`;

const TitleLeft = styled.div<{ $isAI: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.Micro};
  ${({ $isAI }) => $isAI && aiTextGradient()}
  min-width: 0;
`;

const AIcon = styled(Icon)`
  flex-shrink: 0;
`;

const TitleText = styled.div`
  min-width: 0;
  ${ellipsis()};
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: ${(props) => content(props, "secondary")};
  margin-top: ${Spacing.ExtraSmall};
  ${ellipsis()};
`;

const Body = styled.div<{ height?: number; $isAI: boolean }>`
  position: relative;
  padding-right: ${({ $isAI }) => ($isAI ? "23px" : Spacing.Large)};
  padding-bottom: ${({ $isAI }) => ($isAI ? "23px" : Spacing.Large)};
  padding-left: ${({ $isAI }) => ($isAI ? "23px" : Spacing.Large)};
  height: ${(props) => props.height - (props.$isAI ? 1 : 0)}px;
`;

const RadarlyCard = ({
  title,
  subtitle,
  magicFooter,
  isAI,
  children,
  actions,
  statusInfo,
  height = 330,
  onClick,
}: IProps) => (
  <Root onClick={onClick} $isAI={isAI}>
    <Header $isAI={isAI}>
      <Title>
        <TitleLeft $isAI={isAI}>
          {isAI && <AIcon glyph="magic" size="ms" color={AI_GRADIENT.start} />}
          <TitleText>{title}</TitleText>
        </TitleLeft>
        <TitleRight>
          {statusInfo}
          <div>{actions}</div>
        </TitleRight>
      </Title>
      <Subtitle>{subtitle}</Subtitle>
    </Header>
    <Body height={height} $isAI={isAI}>
      {children}
    </Body>
    {magicFooter}
  </Root>
);

export default RadarlyCard;
